import React, { useCallback, useEffect, useRef, useState } from 'react';

type WindowRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

interface DraggableResizableWindowProps {
  title?: React.ReactNode;
  initialRect: WindowRect;
  minWidth?: number;
  minHeight?: number;
  zIndex?: number;
  frameStyle?: React.CSSProperties;
  titleBarStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  onRectChange?: (rect: WindowRect) => void;
  windowRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}

const VIEWPORT_MARGIN = 8;

const DraggableResizableWindow: React.FC<DraggableResizableWindowProps> = ({
  title,
  initialRect,
  minWidth = 280,
  minHeight = 120,
  zIndex = 100,
  frameStyle,
  titleBarStyle,
  contentStyle,
  onRectChange,
  windowRef,
  children
}) => {
  const [rect, setRect] = useState<WindowRect>(initialRect);
  const rectRef = useRef<WindowRect>(initialRect);
  const dragStateRef = useRef<{
    pointerStartX: number;
    pointerStartY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const resizeStateRef = useRef<{
    pointerStartX: number;
    pointerStartY: number;
    originWidth: number;
    originHeight: number;
  } | null>(null);
  const setCombinedRef = useCallback((node: HTMLDivElement | null) => {
    if (!windowRef) {
      return;
    }

    if (typeof windowRef === 'function') {
      windowRef(node);
      return;
    }

    (windowRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [windowRef]);

  const clampRect = useCallback((nextRect: WindowRect): WindowRect => {
    if (typeof window === 'undefined') {
      return nextRect;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const maxWidth = Math.max(minWidth, viewportWidth - VIEWPORT_MARGIN * 2);
    const maxHeight = Math.max(minHeight, viewportHeight - VIEWPORT_MARGIN * 2);

    const clampedWidth = Math.min(Math.max(nextRect.width, minWidth), maxWidth);
    const clampedHeight = Math.min(Math.max(nextRect.height, minHeight), maxHeight);

    const maxX = Math.max(VIEWPORT_MARGIN, viewportWidth - clampedWidth - VIEWPORT_MARGIN);
    const maxY = Math.max(VIEWPORT_MARGIN, viewportHeight - clampedHeight - VIEWPORT_MARGIN);

    const clampedX = Math.min(Math.max(nextRect.x, VIEWPORT_MARGIN), maxX);
    const clampedY = Math.min(Math.max(nextRect.y, VIEWPORT_MARGIN), maxY);

    return {
      x: clampedX,
      y: clampedY,
      width: clampedWidth,
      height: clampedHeight
    };
  }, [minHeight, minWidth]);

  useEffect(() => {
    rectRef.current = rect;
  }, [rect]);

  useEffect(() => {
    setRect(prev => clampRect(prev));
  }, [clampRect]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setRect(prev => clampRect(prev));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clampRect]);

  useEffect(() => {
    if (!onRectChange) {
      return;
    }

    onRectChange(rect);
  }, [rect, onRectChange]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const baseRect = rectRef.current;
      const dragState = dragStateRef.current;
      if (dragState) {
        event.preventDefault();
        const nextRect = clampRect({
          ...baseRect,
          x: dragState.originX + (event.clientX - dragState.pointerStartX),
          y: dragState.originY + (event.clientY - dragState.pointerStartY)
        });
        rectRef.current = nextRect;
        setRect(nextRect);
        return;
      }

      const resizeState = resizeStateRef.current;
      if (resizeState) {
        event.preventDefault();
        const nextWidth = resizeState.originWidth + (event.clientX - resizeState.pointerStartX);
        const nextHeight = resizeState.originHeight + (event.clientY - resizeState.pointerStartY);
        const nextRect = clampRect({
          ...baseRect,
          width: nextWidth,
          height: nextHeight
        });
        rectRef.current = nextRect;
        setRect(nextRect);
      }
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      resizeStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [clampRect]);

  const handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const currentRect = rectRef.current;
    dragStateRef.current = {
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      originX: currentRect.x,
      originY: currentRect.y
    };
  };

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const currentRect = rectRef.current;
    resizeStateRef.current = {
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      originWidth: currentRect.width,
      originHeight: currentRect.height
    };
  };

  return (
    <div
      ref={setCombinedRef}
      style={{
        position: 'fixed',
        left: `${rect.x}px`,
        top: `${rect.y}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '14px',
        border: '1px solid rgba(255,255,255,0.2)',
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
        zIndex,
        ...frameStyle
      }}
    >
      <div
        onPointerDown={handleDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          padding: '0.55rem 0.8rem',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
          backgroundColor: 'rgba(2, 6, 23, 0.55)',
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'none',
          ...titleBarStyle
        }}
      >
        <div
          style={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.92)',
            letterSpacing: '0.03em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {title}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444', opacity: 0.85 }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B', opacity: 0.85 }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', opacity: 0.85 }} />
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          ...contentStyle
        }}
      >
        {children}
      </div>

      <div
        onPointerDown={handleResizeStart}
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: 20,
          height: 20,
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 0%, transparent 45%, rgba(255,255,255,0.38) 45%, rgba(255,255,255,0.38) 55%, transparent 55%, transparent 100%)'
        }}
      />
    </div>
  );
};

export default DraggableResizableWindow;
